import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Skeleton from "@mui/material/Skeleton";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import {
  getAuthNToken,
  getmediaModal
} from "@directv-digital/dds.apis.dtv-ov-handlers";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";
import DtvMediaModal from "./dtv-media-modal";
import {
  reportShowCardKeyboardClick,
  SHOW_CAROUSEL_CARD_DESTINATION,
  SHOW_CAROUSEL_CARD_POSITION
} from "./show-carousal.analytics";
import { parallaxStyles, PARALLAX_TRAVEL } from "./show-carousal-parallax.styles";
import type { SeriesDetailsProjection } from "lib/helper/show-network";

const EMPTY_IMAGE =
  "https://www.directv.com/dtvassets/global/component-assets/guide/EmptySVG.svg";

export interface ShowCarousalParallaxProps {
  id?: string;
  // Cleaned OV resources produced by ShowCarousal (each carries mediaModalData with per-show CTAs).
  cards?: any[];
  // Series-level network name / artwork keyed by seriesId, fetched asynchronously by ShowCarousal
  // for episode-derived cards — an episode resource carries neither of its own. Typed from the
  // route's own declaration so the two sides cannot drift.
  enrichment?: Record<string, SeriesDetailsProjection>;
  // 1 = single row (no parallax); 2 = two rows scrolling in opposite directions. Defaults to 2.
  // CMS delivers this as a string ("1"/"2"), so the type accepts both and the component coerces.
  rows?: number | string;
  backgroundColor?: string;
  color?: string;
  header?: any;
  subHeading?: any;
  mediaModal?: boolean;
  enableChannelInfo?: boolean;
  productType?: string;
  channelLabel?: string;
  disableCardClick?: boolean;
  isLoading?: boolean;
}

const dateToFormat = (value: any): string => {
  const formattedDate = new Date(value);
  return formattedDate.toLocaleString("en-US", {
    year: "2-digit",
    day: "numeric",
    month: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
    hour12: true
  });
};

// Pull a plain text value out of a CMS header/subheading which may be an object, an array or a string.
// CMS header blocks (e.g. the `extraDetails` block the Storyblok `array_header` field uses) carry the
// text in `title`; other shapes use `content` — accept either.
export const toText = (value: any): string => {
  if (!value) {
    return "";
  }
  const node = Array.isArray(value) ? value[0] : value;
  return typeof node === "string" ? node : node?.content || node?.title || "";
};

// How many times to repeat a row's cards so the track fills the viewport (plus `travel` of parallax
// drift on each side, so the drift never exposes an empty edge). Returns 1 when the content already
// fills the width or hasn't been measured yet.
export const computeRepeats = (
  viewportWidth: number,
  singleCopyWidth: number,
  travel: number = PARALLAX_TRAVEL
): number => {
  if (!viewportWidth || !singleCopyWidth || singleCopyWidth <= 0) {
    return 1;
  }
  return Math.max(1, Math.ceil((viewportWidth + 2 * travel) / singleCopyWidth));
};

interface ParallaxCardProps {
  card: any;
  copy: number;
  // Section id, used to scope the network line's DOM id so two carousels on one page cannot collide.
  sectionId?: string;
  isLoading?: boolean;
  disableCardClick?: boolean;
  mediaModal?: boolean;
  onCardClick: (card: any) => void;
}

// Memoized so a modal open/close (which lives on the parent) doesn't re-render every poster. Only
// re-renders when its own props change; `onCardClick` is a stable useCallback from the parent.
// Exported for the spec, alongside this file's other test-visible internals.
export const ParallaxCard = React.memo(({
  card,
  copy,
  sectionId = "dtv-show-carousal-parallax",
  isLoading,
  disableCardClick,
  mediaModal,
  onCardClick
}: ParallaxCardProps) => {
  const isMock =
    isLoading ||
    !card?.resourceId ||
    card?.resourceType === "mock" ||
    !card?.image?.imageUrl;
  const title = card?.mediaModalData?.heading || card?.title || "";
  // Network only — never the genre list (AC2). ShowCarousal resolves it from the resource's own
  // network fields, so nothing here has to parse the concatenated modal subheading.
  const network = card?.network || "";
  // Copies beyond the first are visual gap-fill only: hide them from assistive tech and the tab
  // order (and skip duplicate analytics markup) so each show is announced/focusable once.
  const decorative = copy > 0;
  // A card is only a button when it can actually open the modal. When interaction is disabled
  // (disableCardClick / mediaModal === false) it must not carry button semantics or analytics,
  // otherwise it's a focusable, tracked control that does nothing.
  const interactive = !decorative && !disableCardClick && mediaModal !== false;
  // `role="button"` makes the label text presentational, so the network line is not part of the
  // card's accessible name. It is exposed as the accessible *description* instead: the name stays
  // the analytics link value (getAnalyticsAttrs emits aria-label as its link-name channel, and GTM /
  // Tealium auto-event tracking reads DOM attributes off the clicked element — overriding it would
  // silently change the reported link name). The id is scoped by section (two carousels on one page
  // can hold the same show); it needs no copy scoping because only the announced copy emits it at
  // all — a repeat-to-fill copy is decorative, and decorative is never interactive. A card the CMS
  // configured non-clickable gets no description either, and its network line stays hidden (no
  // hover, no focus) — a known gap being tracked with UX.
  const networkId = interactive && network ?
    `${sectionId}-network-${card?.resourceId}` :
    undefined;

  if (isMock) {
    return (
      <Card
        aria-hidden={decorative || undefined}
        sx={parallaxStyles.card(card?.bgColor)}
      >
        <Skeleton
          animation="wave"
          height="100%"
          variant="rectangular"
          width="100%"
        />
      </Card>
    );
  }
  return (
    <Card
      aria-describedby={networkId}
      aria-hidden={decorative || undefined}
      aria-label={interactive ? title || "carousel card" : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : -1}
      onClick={interactive ? () => onCardClick(card) : undefined}
      onKeyDown={event => {
        // A held key repeats; one activation is one event.
        if (
          interactive &&
          !event.repeat &&
          (event.key === "Enter" || event.key === " ")
        ) {
          event.preventDefault();
          // Keyboard activation fires no DOM click, so the card's data attributes are
          // never read and the click has to be reported explicitly.
          reportShowCardKeyboardClick(title);
          onCardClick(card);
        }
      }}
      {...(interactive ?
        getAnalyticsAttrs(
          title?.toLowerCase?.(),
          SHOW_CAROUSEL_CARD_POSITION,
          SHOW_CAROUSEL_CARD_DESTINATION
        ) :
        {})}
      sx={interactive ?
        parallaxStyles.card(card?.bgColor) :
        {
          ...parallaxStyles.card(card?.bgColor),
          pointerEvents: "none"
        }}
    >
      <CardMedia
        alt=""
        component="img"
        loading="lazy"
        src={card?.image?.imageUrl || EMPTY_IMAGE}
        sx={parallaxStyles.cardImage}
        onError={event => {
          const target = event.target as HTMLImageElement;
          target.onerror = null;
          target.src = card?.image?.defaultImageUrl || EMPTY_IMAGE;
        }}
      />
      {title && (
        <DtvBox className="parallax-label" sx={parallaxStyles.label}>
          {/* body3medium / body3 ARE the comp's `Body 3 medium` / `Body 3` styles: roboto-medium
              and roboto-regular at a flat 14px, declared for every breakpoint. Sizing the label
              from `sx` instead does not work — the variant's per-media-query sizes are emitted
              after the flat `sx` value and win below the largest desktop breakpoint (AC1). */}
          <DtvText
            className="parallax-label-title"
            content={title}
            variant="body3medium"
            sx={parallaxStyles.labelTitle}
          />
          {network && (
            <DtvBox
              className="parallax-label-network"
              id={networkId}
              sx={parallaxStyles.labelNetwork}
            >
              <DtvText
                content={`Watch on ${network}`}
                variant="body3"
                sx={parallaxStyles.labelNetworkText}
              />
            </DtvBox>
          )}
        </DtvBox>
      )}
    </Card>
  );
});

// CMS delivers `rows` as a string ("1"/"2"), so coerce before the single-row check.
// Anything other than 1 (incl. undefined) falls back to the default two-row layout.
export const resolveRowCount = (rows?: number | string): 1 | 2 => (Number(rows) === 1 ? 1 : 2);

export function ShowCarousalParallax(props: ShowCarousalParallaxProps) {
  const rowCount = resolveRowCount(props?.rows);
  // One id for the section element and for scoping per-card DOM ids inside it.
  const sectionId = props?.id || "dtv-show-carousal-parallax";
  // Memoized so the child grid / React.memo cards aren't invalidated by a new array each parent render.
  const cards = useMemo(
    () => (Array.isArray(props?.cards) ? props.cards : []),
    [props?.cards]
  );
  // Merged cards, cached per (card, its own enrichment entry). The enrichment map arrives in waves
  // — a new map identity per chunk of series ids, with the entries resolved earlier carried over by
  // reference — so without this cache every already-merged card would get a fresh object, and
  // re-render with byte-identical props, on every later wave.
  const mergedCardsRef = useRef(
    new Map<any, { enriched: SeriesDetailsProjection; merged: any }>()
  );
  // Fold the async series enrichment in once, here, so every ParallaxCard still receives a single
  // already-resolved card. Passing the map down instead would hand each memoized card a new object
  // on every parent render and re-render the whole row.
  const enrichedCards = useMemo(() => {
    const enrichment = props?.enrichment;
    if (!enrichment || !Object.keys(enrichment).length) {
      mergedCardsRef.current = new Map();
      return cards;
    }
    const previous = mergedCardsRef.current;
    // Rebuilt from this pass alone, so cards a refetch replaced don't stay reachable through it.
    const next = new Map<any, { enriched: SeriesDetailsProjection; merged: any }>();
    const result = cards.map(card => {
      const enriched = card?.seriesId ? enrichment[card.seriesId] : undefined;
      if (!enriched) {
        return card;
      }
      const hit = previous.get(card);
      if (hit?.enriched === enriched) {
        next.set(card, hit);
        return hit.merged;
      }
      // The resource's own network always wins; enrichment only fills what the feed omitted.
      // The image is different: an episode card already has a still, so this substitutes a better
      // asset rather than filling an empty slot. Limiting it to episode-derived cards with a
      // non-empty enriched url is also what keeps the skeleton path — which keys off a missing
      // image url — out of reach.
      const image = card?.contentType === "EPISODE" && enriched?.image?.imageUrl ?
        enriched.image :
        card?.image;
      const merged = {
        ...card,
        network: card?.network || enriched?.network || "",
        image
      };
      next.set(card, {
        enriched,
        merged
      });
      return merged;
    });
    mergedCardsRef.current = next;
    return result;
  }, [cards, props?.enrichment]);
  const headingText = toText(props?.header);
  const subheadingText = toText(props?.subHeading);

  const [mediaModalData, setMediaModalData] = useState<any>({});
  const sectionRef = useRef<HTMLDivElement | null>(null);
  // parallaxLayer elements (scroll-linked translateX, one per row)
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  // clipping viewport + card track elements, measured to size the repeat-to-fill (one per row)
  const viewportRefs = useRef<Array<HTMLDivElement | null>>([]);
  const trackRefs = useRef<Array<HTMLDivElement | null>>([]);
  const frameRef = useRef<number | null>(null);

  // How many times each row's cards are repeated to fill the viewport (see computeRepeats).
  const [repeats, setRepeats] = useState(1);

  // Whether the user prefers reduced motion. SSR-safe: defaults to false (motion-capable markup,
  // matching the server render) and is corrected on the client. When true the scroll-linked drift
  // is disabled, so the viewport falls back to a native swipe/scroll container (see below) to keep
  // every card reachable instead of clipping the row to whatever fits.
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(query.matches);
    sync();
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", sync);
      return () => query.removeEventListener("change", sync);
    }
    // Safari < 14 / older WebViews only expose the deprecated addListener API.
    if (typeof query.addListener === "function") {
      query.addListener(sync);
      return () => query.removeListener(sync);
    }
    return undefined;
  }, []);

  // The drift only runs with two rows and motion allowed; otherwise the rows are static and the
  // viewport must stay scrollable so off-screen cards remain reachable by swipe/keyboard.
  const driftActive = rowCount >= 2 && !reducedMotion;

  // Interleave cards across rows so each row is evenly populated.
  const rowItems = useMemo<any[][]>(() => {
    const rows: any[][] = Array.from({ length: rowCount }, () => []);
    enrichedCards.forEach((card, index) => {
      rows[index % rowCount].push({
        card,
        index
      });
    });
    return rows;
  }, [enrichedCards, rowCount]);

  const closeMediaModal = useCallback(() => setMediaModalData({}), []);

  const handleCardClick = useCallback(
    async (card: any) => {
      if (!card?.resourceId || props?.disableCardClick) {
        return;
      }
      if (props?.mediaModal === false) {
        return;
      }
      if (!props?.enableChannelInfo) {
        setMediaModalData({
          ...card.mediaModalData,
          modalOpen: true
        });
        return;
      }
      const contentType =
        card?.resourceType?.toLowerCase() === "series" ?
          "tv-shows" :
          card?.resourceType?.toLowerCase() === "network" ?
            "networks" :
            "movies";
      const browserId =
        props?.productType === "stream" ?
          "DFW_WEB_Chrome_G" :
          "DTVE_DFW_WEB_Chrome_G";
      try {
        const accessTokenResponse = await getAuthNToken(browserId);
        const data: any = await getmediaModal(
          contentType,
          card.resourceId,
          accessTokenResponse?.access_token
        );
        const channelNumber: string =
          data?.consumables?.[0]?.channel?.majorChannelNumber || "";
        const startTime = data?.consumables?.[0]?.startTime;
        setMediaModalData({
          ...card.mediaModalData,
          heading: data?.displayTitle ?
            data.displayTitle.toLowerCase() :
            card?.mediaModalData?.heading,
          body: startTime ? dateToFormat(startTime) : "",
          subheading: channelNumber ?
            `${props?.channelLabel} ${channelNumber}` :
            "",
          modalOpen: true
        });
      } catch (_error) {
        console.warn(
          "[ShowCarousalParallax] media-modal fetch failed; opening modal without channel info",
          _error
        );
        setMediaModalData({
          ...card.mediaModalData,
          modalOpen: true
        });
      }
    },
    [
      props?.disableCardClick,
      props?.mediaModal,
      props?.enableChannelInfo,
      props?.productType,
      props?.channelLabel
    ]
  );

  // Scroll-linked parallax at every breakpoint (AC3). Disabled only for a single row or when the
  // user prefers reduced motion (in which case the static rows scroll natively instead).
  useEffect(() => {
    if (typeof window === "undefined" || !driftActive) {
      return undefined;
    }

    let lastProgress = -1;
    const update = () => {
      const section = sectionRef.current;
      if (!section) {
        return;
      }
      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      // 0 when the section is entering from the bottom, 1 when it has scrolled past the top.
      const progress = Math.min(
        Math.max(
          (viewportHeight - rect.top) / (viewportHeight + rect.height),
          0
        ),
        1
      );
      // Skip DOM writes when the section is visible but the scroll position hasn't moved.
      if (Math.abs(progress - lastProgress) < 0.0005) {
        return;
      }
      lastProgress = progress;
      rowRefs.current.forEach((rowEl, rowIndex) => {
        if (!rowEl) {
          return;
        }
        const direction = rowIndex % 2 === 0 ? 1 : -1;
        // A constant -PARALLAX_TRAVEL overhang keeps the drift within the padded track: the offset
        // ranges [-2*travel, 0], so the left edge is always <= 0 and the right edge always >=
        // viewport (the track is >= viewport + 2*travel wide via computeRepeats). Without it the
        // drift would slide content sideways and expose an empty edge as the page scrolls.
        const drift = (progress - 0.5) * 2 * PARALLAX_TRAVEL * direction;
        rowEl.style.transform = `translate3d(${drift - PARALLAX_TRAVEL}px, 0, 0)`;
      });
    };

    // Drive the parallax from a rAF loop that runs only while the section is on screen, rather
    // than from window "scroll" events. The app does not scroll on window (the document/body is
    // laid out full-height and scrolling happens off the window), so scroll events never reached
    // this component and the rows stayed frozen. Reading getBoundingClientRect each frame works
    // regardless of which element actually scrolls.
    let running = false;
    const tick = () => {
      update();
      if (running) {
        frameRef.current = window.requestAnimationFrame(tick);
      }
    };
    const start = () => {
      if (!running) {
        running = true;
        frameRef.current = window.requestAnimationFrame(tick);
      }
    };
    const stop = () => {
      running = false;
      if (frameRef.current != null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };

    update();
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          start();
        } else {
          stop();
          update();
        }
      },
      { threshold: 0 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    window.addEventListener("resize", update);
    return () => {
      stop();
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [driftActive, enrichedCards.length]);

  // When the drift is off (reduced motion / single row), clear any transform the rAF left on the
  // rows so they rest at their natural position and scroll natively rather than sitting at an
  // overhang offset.
  useEffect(() => {
    if (!driftActive) {
      rowRefs.current.forEach(rowEl => {
        if (rowEl) {
          rowEl.style.transform = "";
        }
      });
    }
  }, [driftActive]);

  // ---- Repeat-to-fill measurement ---------------------------------------------------------
  // Width of ONE copy of a row's cards (invariant of the current repeat count) plus the viewport
  // width. The cards share one flex `gap`, so the rendered `scrollWidth` folds in the inter-copy
  // gaps; back them out via `scrollWidth + gap = repeats * (oneCopy + gap)`. This keeps the derived
  // single-copy width stable across repeat counts (otherwise `repeats` oscillates and never settles).
  const getRowMetrics = useCallback(
    (rowIndex: number) => {
      const track = trackRefs.current[rowIndex];
      const viewport = viewportRefs.current[rowIndex];
      if (!track || !viewport || !viewport.clientWidth) {
        return null;
      }
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const copies = Math.max(1, repeats);
      return {
        singleCopyWidth: (track.scrollWidth + gap) / copies - gap,
        viewportWidth: viewport.clientWidth
      };
    },
    [repeats]
  );

  // Repeat each row's cards enough times to span the viewport (plus the parallax-drift overhang).
  // Repeat-to-fill exists only to hide that overhang, so when the drift is off (reduced motion /
  // single row) the viewport scrolls natively and one copy is correct — never duplicate cards there.
  const measureRepeats = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    const neededRepeats = !driftActive ?
      1 :
      trackRefs.current.reduce((max, _track, rowIndex) => {
        const metrics = getRowMetrics(rowIndex);
        return metrics ?
          Math.max(
            max,
            computeRepeats(metrics.viewportWidth, metrics.singleCopyWidth)
          ) :
          max;
      }, 1);
    if (neededRepeats !== repeats) {
      setRepeats(neededRepeats);
    }
  }, [driftActive, getRowMetrics, repeats]);

  useEffect(() => {
    measureRepeats();
    const onResize = () => measureRepeats();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [rowCount, enrichedCards.length, driftActive, measureRepeats]);

  // The card grid only depends on the card data / layout — NOT on `mediaModalData` — so opening or
  // closing the details modal no longer re-renders every poster (paired with the React.memo card).
  const grid = useMemo(
    () => rowItems.map((items, rowIndex) => (
        <DtvBox
          key={`parallax-viewport-${rowIndex}`}
          ref={el => {
            viewportRefs.current[rowIndex] = el as HTMLDivElement | null;
          }}
          sx={parallaxStyles.viewport(driftActive)}
        >
          <DtvBox
            ref={el => {
              rowRefs.current[rowIndex] = el as HTMLDivElement | null;
            }}
            sx={parallaxStyles.parallaxLayer}
          >
            <DtvBox
              ref={el => {
                trackRefs.current[rowIndex] = el as HTMLDivElement | null;
              }}
              sx={parallaxStyles.track}
            >
              {Array.from({ length: repeats }).flatMap((_, copy) => items.map(entry => (
                  <ParallaxCard
                    key={`${entry.card?.resourceId ?? "mock"}-${copy}-${entry.index}`}
                    card={entry.card}
                    copy={copy}
                    sectionId={sectionId}
                    isLoading={props?.isLoading}
                    disableCardClick={props?.disableCardClick}
                    mediaModal={props?.mediaModal}
                    onCardClick={handleCardClick}
                  />
                )))}
            </DtvBox>
          </DtvBox>
        </DtvBox>
    )),
    [
      rowItems,
      repeats,
      driftActive,
      sectionId,
      props?.isLoading,
      props?.disableCardClick,
      props?.mediaModal,
      handleCardClick
    ]
  );

  try {
    return (
      <DtvBox
        ref={sectionRef}
        id={sectionId}
        sx={parallaxStyles.section(props?.backgroundColor)}
      >
        {headingText && (
          <DtvText
            content={headingText}
            variant="h3"
            sx={parallaxStyles.heading(props?.color)}
          />
        )}
        {subheadingText && (
          <DtvText
            content={subheadingText}
            variant="subhead2medium"
            sx={parallaxStyles.subheading(props?.color)}
          />
        )}
        <DtvBox sx={parallaxStyles.rowsWrapper}>{grid}</DtvBox>
        <DtvMediaModal {...mediaModalData} closeMediaModal={closeMediaModal} />
      </DtvBox>
    );
  } catch (error) {
    return <ErrorDisplay component="Show Carousal Parallax" error={error} />;
  }
}

export default ShowCarousalParallax;
